/**
 * Reusable user-avatar. Renders the profile image when available;
 * falls back to a brand-colored initials chip on missing URL or load
 * failure. Mirrors the in-app top-bar avatar in Layout.tsx — kept as
 * a separate component because that one is tied to the `MeResponse`
 * shape and this one accepts the loose "anyone with email + name +
 * optional avatar" shape every other user surface uses.
 */

import { useState } from 'react';

export interface AvatarUser {
  email: string;
  name?: string | null;
  /** Snake-case for shapes coming from /api/* responses. */
  avatar_url?: string | null;
  /** Camel-case for shapes coming from the auth `MeResponse`. */
  avatarUrl?: string | null;
}

export interface AvatarProps {
  user: AvatarUser;
  /** Pixel size. Defaults to 24 (typical inline list rows). */
  size?: number;
  className?: string;
}

function initialsFor(user: AvatarUser): string {
  const source = (user.name ?? '').trim() || user.email.split('@')[0] || '?';
  const parts = source.split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

export function Avatar({ user, size = 24, className = '' }: AvatarProps) {
  const [broken, setBroken] = useState(false);
  const url = user.avatar_url ?? user.avatarUrl ?? null;
  if (url && !broken) {
    return (
      <img
        src={url}
        alt={user.name ?? user.email}
        width={size}
        height={size}
        loading="lazy"
        onError={() => setBroken(true)}
        className={`rounded-full bg-surface-2 object-cover flex-none ring-1 ring-line ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={`rounded-full bg-brand text-brand-fg flex-none flex items-center justify-center font-semibold ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.4) }}
    >
      {initialsFor(user)}
    </span>
  );
}
